import React from "react";
import { Box, Typography, makeStyles } from "@material-ui/core";
import { getLang } from "app/feature/constants";
import { Skeleton } from "@material-ui/lab";
import moment from "moment";
import LdDefaultBannerImg from "assets/img/ld_default_banner_img.png";
import { ErrorSVG } from "modules/lucky-draw/utils/static";

const useStyles = makeStyles((theme) => ({
  container: {
    position: "relative",
    borderRadius: 16,
    minHeight: 143,
    display: "flex",
    justifyContent: "space-between",
    scrollbarWidth: "thin",
    backgroundColor: "#ffffff",
  },
  label: {
    background:
      "linear-gradient(90deg, #6BDA8E 0%, #4D98D9 34.8%, #7077F7 62.65%, #CB62F6 100%);",
    padding: "2px 8px",
    borderRadius: 8,
    textTransform: "uppercase",
    fontWeight: 900,
    color: "white",
    width: "fit-content",
    textShadow: "-1px 0 #67D099, 1px 0 #949BC8, 1px 0 #AA80DF, 1px 0 #C065F6",
  },
  bannerContainer: {
    borderRadius: 16,
    padding: 16,
    position: "relative",
    background: "linear-gradient(95.48deg, #1A385E 50.93%, #461E70 100%)",
    color: "white",
    overflow: "hidden",
  },
  bannerDefaultImg: {
    position: "absolute",
    width: "40%",
    height: "90%",
    right: 0,
    bottom: 0,
    "& img": {
      height: "100%",
      width: "100%",
      objectFit: "contain",
    },
  },
  defaultThemeBannerCountdown: {
    position: "absolute",
    right: 0,
    top: 8,
    backgroundColor: "#FFFFFFCC",
    color: "black",
    padding: "2px 12px",
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
  },
  bannerImg: {
    width: "100%",
    height: "auto",
    borderRadius: 8,
  },
  bannerCountdown: {
    position: "absolute",
    right: 0,
    top: 10,
    backgroundColor: "#1A385EB2",
    color: "white",
    padding: "4px 12px",
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
  },
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
  },
  button: {
    animation: "$breathing 4s ease-in-out infinite normal",
    willChange: "transform",
    width: "100%",
    height: 40,
    border: "none",
    borderRadius: 10,
    background: "linear-gradient(to right, #77530a, #ffd277, #77530a, #77530a, #ffd277, #77530a)",
    backgroundSize: "250%",
    backgroundPosition: "left",
    color: "#ffd277",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transitionDuration: "1s",
    overflow: "hidden",
    fontFamily: "Arial, Helvetica, sans-serif",
    textDecoration: "none",
    userSelect: "none",
    fontSize: 15,
    "&::before": {
      position: "absolute",
      content: '""',
      inset: "3px",
      borderRadius: 8,
      backgroundColor: "#800000",
    },
    "& span": {
      zIndex: 1,
      display: "block",
    },
    "&:hover": {
      backgroundPosition: "right",
      transitionDuration: "1s",
    },
    "&:hover::before": {
      backgroundPosition: "right",
      transitionDuration: "1s",
    },
    "&:active": {
      transform: "scale(0.95)",
    },
    "&:disabled": {
      animation: "none",
      pointerEvents: "none",
      opacity: 0.65,
      color: "#7e7e7e",
      background: "#dcdcdc",
      boxShadow: "none",
      textShadow: "none",
    },
  },
  "@keyframes breathing": {
    "0%": {
      transform: "scale(0.95)",
    },
    "25%": {
      transform: "scale(1)",
    },
    "50%": {
      transform: "scale(0.95)",
    },
    "75%": {
      transform: "scale(1)",
    },
    "100%": {
      transform: "scale(0.95)",
    },
  },
}));

export default function SurveyDetailCardComponent({
  luckyDraw,
  lang,
  isLoading,
  uiSetting,
  countdown,
  drawCountdown,
}) {
  const classes = useStyles();

  let isJoinStart = luckyDraw
    ? moment().isAfter(
        moment
          .utc(
            luckyDraw.version === "legacy"
              ? luckyDraw.start_at
              : luckyDraw.join_open_at
          )
          .local()
      )
    : false;
  let isJoinEnd =
    luckyDraw?.version === "legacy"
      ? moment().isAfter(moment.utc(luckyDraw?.end_at).local())
      : luckyDraw?.join_close_at
      ? moment().isAfter(moment.utc(luckyDraw?.join_close_at).local())
      : false;
  let isEnded = luckyDraw?.end_at
    ? moment().isAfter(moment.utc(luckyDraw?.end_at).local())
    : false;

  let showCountdown =
    (countdown.day > 0 ||
      countdown.hour > 0 ||
      countdown.min > 0 ||
      countdown.sec > 0) &&
    !isEnded;
  let showDrawCountdown =
    (drawCountdown.day > 0 ||
      drawCountdown.hour > 0 ||
      drawCountdown.min > 0 ||
      drawCountdown.sec > 0) &&
    !isEnded;

  const formatTwoDigits = (value) => {
    return Number(value) < 10 ? `0${value}` : value;
  };

  return (
    <>
      <Box className={classes.container}>
        <Box style={{ width: "100%" }}>
          {isLoading ? (
            <Box
              p={2}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              style={{ width: "100%" }}
            >
              <Skeleton width={"100%"} height={30} />
              <Skeleton width={"100%"} height={30} />
              <Skeleton width={"100%"} height={30} />
              <Skeleton width={"100%"} height={30} />
            </Box>
          ) : luckyDraw ? (
            !luckyDraw.image_url ? (
              <Box className={classes.bannerContainer}>
                <Box className={classes.label}>
                  <Typography variant="h6">
                    <b>{getLang(lang, "label.LUCKY_DRAW")}</b>
                  </Typography>
                </Box>
                <Box style={{ width: "60%" }} mt={2}>
                  <Box mb={0.5}>
                    <Typography variant="h5">
                      <b>{luckyDraw.title}</b>
                    </Typography>
                  </Box>
                  <Typography variant="body2">
                    {luckyDraw.description}
                  </Typography>
                </Box>
                <Box mt={3} mb={2}>
                  <button
                    className={classes.button}
                    style={{
                      border: "1px solid black",
                    }}
                  >
                    <Typography
                      variant="body1"
                      style={{ textTransform: "capitalize", zIndex: 1 }}
                    >
                      <b>
                        {getLang(
                          lang,
                          isJoinStart ? "label.PLAY_NOW" : "label.EXPLORE_NOW"
                        )}
                      </b>
                    </Typography>
                  </button>
                </Box>
                <Box className={classes.bannerDefaultImg}>
                  <img src={LdDefaultBannerImg} alt="ld" />
                </Box>
                {isJoinEnd &&
                luckyDraw.join_end_action === 1 &&
                luckyDraw.draw_at &&
                showDrawCountdown ? (
                  <Box className={classes.defaultThemeBannerCountdown}>
                    <Typography variant="caption">
                      {getLang(lang, "label.LIVE_IN")}
                      <span>
                        <b>
                          {drawCountdown.day}:
                          {formatTwoDigits(drawCountdown.hour)}:
                          {formatTwoDigits(drawCountdown.min)}:
                          {formatTwoDigits(drawCountdown.sec)}
                        </b>
                      </span>
                    </Typography>
                  </Box>
                ) : (
                  showCountdown &&
                  (!isJoinStart || !isJoinEnd) && (
                    <Box className={classes.defaultThemeBannerCountdown}>
                      <Typography variant="caption">
                        {isJoinStart
                          ? getLang(lang, "label.ENDS_IN")
                          : getLang(lang, "label.STARTS_IN")}{" "}
                        <span>
                          <b>
                            {countdown.day}:{formatTwoDigits(countdown.hour)}:
                            {formatTwoDigits(countdown.min)}:
                            {formatTwoDigits(countdown.sec)}
                          </b>
                        </span>
                      </Typography>
                    </Box>
                  )
                )}
                {isEnded && (
                  <Box className={classes.overlay}>
                    <Typography variant="body1">
                      {getLang(lang, "label.ENDED")}
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <Box
                className={classes.bannerContainer}
                style={{ background: "white", color: "black" }}
              >
                <img
                  src={luckyDraw.image_url}
                  alt="lucky-draw-img"
                  className={classes.bannerImg}
                />
                {isJoinEnd &&
                luckyDraw.join_end_action === 1 &&
                luckyDraw.draw_at &&
                showDrawCountdown ? (
                  <Box className={classes.bannerCountdown}>
                    <Typography variant="caption">
                      {getLang(lang, "label.LIVE_IN")}{" "}
                      <span>
                        <b>
                          {drawCountdown.day}:
                          {formatTwoDigits(drawCountdown.hour)}:
                          {formatTwoDigits(drawCountdown.min)}:
                          {formatTwoDigits(drawCountdown.sec)}
                        </b>
                      </span>
                    </Typography>
                  </Box>
                ) : (
                  showCountdown &&
                  (!isJoinStart || !isJoinEnd) && (
                    <Box className={classes.bannerCountdown}>
                      <Typography variant="caption">
                        {isJoinStart
                          ? getLang(lang, "label.ENDS_IN")
                          : getLang(lang, "label.STARTS_IN")}{" "}
                        <span>
                          <b>
                            {countdown.day}:{formatTwoDigits(countdown.hour)}:
                            {formatTwoDigits(countdown.min)}:
                            {formatTwoDigits(countdown.sec)}
                          </b>
                        </span>
                      </Typography>
                    </Box>
                  )
                )}
                <Box mt={1}>
                  <Box
                    display="flex"
                    alignItems="center"
                    mb={0.5}
                    flexWrap="wrap"
                  >
                    <Box mr={1} mt={0.5}>
                      <Typography variant="body1">
                        <b>{luckyDraw.title}</b>
                      </Typography>
                    </Box>
                    <Box className={classes.label} mt={0.5}>
                      <Typography variant="body2">
                        <b>{getLang(lang, "label.LUCKY_DRAW")}</b>
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body1">
                    {luckyDraw.description}
                  </Typography>
                </Box>
                <Box mt={2} mb={1}>
                  <button
                    className={classes.button}
                    style={{
                      border: "1px solid black",
                    }}
                  >
                    <Typography
                      variant="body1"
                      style={{ textTransform: "capitalize", zIndex: 1 }}
                    >
                      <b>
                        {getLang(
                          lang,
                          isJoinStart ? "label.PLAY_NOW" : "label.EXPLORE_NOW"
                        )}
                      </b>
                    </Typography>
                  </button>
                </Box>
                {isEnded && (
                  <Box className={classes.overlay}>
                    <Typography variant="body1">
                      {getLang(lang, "label.ENDED")}
                    </Typography>
                  </Box>
                )}
              </Box>
            )
          ) : null}
        </Box>
      </Box>
      {isEnded && (
        <Box display="flex" alignItems="center" mt={1}>
          <Box mr={1}>
            <ErrorSVG />
          </Box>
          <Typography variant="caption" style={{ color: "#3a4d54db" }}>
            {getLang(lang, "paragraph.THIS_CARD_HIDDEN_IN_LANDING_PAGE")}
          </Typography>
        </Box>
      )}
    </>
  );
}
